import styled from 'styled-components';
import { Container } from 'ui';
import { decodeId, formatCurrencyLabel } from 'utils';
import { formatDateTime } from '@koob/dates';
import { useTranslation } from 'react-i18next';
import ToConnectionRequestState from 'components/ToConnectionRequestState';
import { useAsync } from 'react-async';
import { internalPut } from 'api/node';
import { useState } from 'react';
import Header from './Partials/Header';
import { useApp, useNotifications, useSearchParams, useSnack } from 'hooks';
import LocationAwareSearch from 'components/LocationAwareSearch';
import { getToConnectionRequests } from 'api/node/toConnectionRequest';
import {
  KoobButton,
  KoobDataTable,
  KoobInputSegmented,
  KoobInputSelect,
  KoobListPageLayout,
  KoobTableActionMenu,
  KoobTableActionMenuItem,
  KoobTooltip,
  koobTableUrlSort,
} from '@koob/ui';
import { Link } from 'react-router-dom';
import { markNotificationAsRead } from 'api/node/notifications';
import NotificationType from 'constants/NotificationType';
import { getCurrencies } from 'api/node/currencies';
import { getOrganizations } from 'api/node/organization';
import { isEqual } from 'lodash';

const DateWrapper = styled.span`
  text-transform: capitalize;
`;

const ToConnectionRequestList = () => {
  const { t } = useTranslation('toConnectionRequest');
  const {
    isAllowedTo,
    canManageToConnectionRequest,
    user,
    currentOrganization,
    selectedDistributionOrganization,
    setSelectedDistributionOrganization,
  } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const { order, direction, search } = searchParams;
  const { sort, onSortChange } = koobTableUrlSort(
    searchParams,
    setSearchParams,
  );

  const { data, reload, isLoading } = useAsync({
    promiseFn: getToConnectionRequests,
    filters: {
      order,
      direction,
    },
    watchFn: (props, prevProps) => {
      return !isEqual(props.filters, prevProps.filters);
    },
  });
  const [selectedOrganization, setSelectedOrganization] = useState({
    label: currentOrganization?.displayName,
    value: currentOrganization?.organizationId,
    currency: currentOrganization?.currency,
  });
  const { notify } = useSnack();
  const { notifications } = useNotifications();

  const { data: currenciesData, isLoading: currenciesLoading } = useAsync({
    promiseFn: getCurrencies,
  });
  const currenciesOptions = (currenciesData?.data || []).map(
    ({ name: currency }) => ({
      value: currency,
      label: formatCurrencyLabel(currency),
    }),
  );

  const { data: organizationDatas } = useAsync({
    promiseFn: getOrganizations,
    filters: {
      isPartner: false,
      discarded: false,
    },
  });

  const organizationOptions = (organizationDatas?.data ?? [])?.map(
    ({ id, name, currency }) => ({
      label: name,
      value: id,
      currency,
    }),
  );

  const handleOrganizationChange = (organization) => {
    setSelectedOrganization(organization);
    setSelectedDistributionOrganization(organization);
  };

  const handleToogle = async (value, request, kind) => {
    const payload =
      kind === 'cancelCondition'
        ? {
            partnerCancelCondition: value === 'to',
          }
        : {
            showToTa: value,
          };
    await internalPut(
      `/to-connection-requests/${request?.id}/quickModification`,
      payload,
    );
    reload();
    notify(t('updated'));
  };

  const modifyCurrency = async (value, request) => {
    await internalPut(
      `/to-connection-requests/${request?.id}/quickModification`,
      {
        currency: value,
      },
    );
    reload();
    notify(t('updatedTOCurrency'));
  };

  const getNotificationsByProductType = (id, type) => {
    return notifications.filter(
      (notification) =>
        !notification.read &&
        notification.relatedObject === id &&
        notification.notificationType === type,
    );
  };

  const readConnectionRequestNotifications = async (notifications) => {
    if (!notifications?.length) {
      return;
    }

    for (const notification of notifications) {
      await markNotificationAsRead({ notificationId: notification?.id });
    }
  };

  const request = data?.data || [];
  const organizationIdToFilter = selectedOrganization.value;
  const formatResult = Object.values(request).reduce((acc, curr) => {
    if (
      curr.dmcOrganization?.id?.toString() !==
      decodeId(organizationIdToFilter)?.toString()
    ) {
      return acc; // Skip requests not related to the selected organization
    }

    const id = curr.toOrganization.id;
    const type = curr.type;
    const index = acc.findIndex((v) => v.toOrganization.id === id);
    if (index !== -1) {
      acc[index] = {
        ...acc[index],
        [type]: curr,
      };
    } else {
      acc = [...acc, { ...curr, [type]: curr }];
    }
    return acc;
  }, []);

  const isAllowedToManage = () => {
    return (
      isAllowedTo('canManageToConnectionRequest') ||
      canManageToConnectionRequest(user)
    );
  };

  const requests = (formatResult || []).filter((r) =>
    search
      ? r?.toOrganization?.name.toLowerCase().indexOf(search.toLowerCase()) > -1
      : true,
  );

  const columns = [
    {
      key: 'companyName',
      label: (
        <span className="whitespace-normal text-center">
          {t('headings.organisation')}
        </span>
      ),
      width: '16%',
      sortable: true,
      sortKey: 'organization',
      render: (request) => (
        <div className="font-medium">{request?.toOrganization?.name}</div>
      ),
    },
    {
      key: 'createdAt',
      label: (
        <span className="whitespace-normal text-center">
          {t('headings.created')}
        </span>
      ),
      width: '10%',
      sortable: true,
      render: (request) => (
        <DateWrapper>
          {formatDateTime(
            request?.toOrganization?.toRequestAccess?.createdAt ??
              request?.toOrganization?.createdAt,
            'MMM DD, YYYY',
          )}
        </DateWrapper>
      ),
    },
    {
      key: 'products',
      label: (
        <KoobTooltip tip={t('headings.productsStatus')}>
          <span className="whitespace-normal text-center">
            {t('headings.products')}
          </span>
        </KoobTooltip>
      ),
      width: '12%',
      render: (request) => (
        <div className="flex justify-center space-x-3">
          <ToConnectionRequestState
            type="hotel"
            state={request?.hotel?.state}
            updating={!!request?.hotel?.activeJob}
          />
          <ToConnectionRequestState
            type="experience"
            state={request?.experience?.state}
            updating={!!request?.experience?.activeJob}
          />
          <ToConnectionRequestState
            type="template"
            state={request?.template?.state}
            updating={!!request?.template?.activeJob}
          />
        </div>
      ),
    },
    {
      key: 'apply_conditions_to',
      label: (
        <KoobTooltip tip={t('headings.contractingCondition')}>
          <span className="whitespace-normal text-center">
            {t('headings.condition')}
          </span>
        </KoobTooltip>
      ),
      width: '14%',
      render: (request) => (
        <KoobInputSegmented
          value={request?.partnerCancelCondition ? 'to' : 'dmc'}
          onChange={(value) => handleToogle(value, request, 'cancelCondition')}
          options={[
            { value: 'dmc', label: 'DMC' },
            { value: 'to', label: 'TO' },
          ]}
          disabled={!isAllowedToManage()}
        />
      ),
    },
    {
      key: 'showToTa',
      label: (
        <KoobTooltip tip={t('headings.shareTOTA')}>
          <span className="whitespace-normal text-center">
            {t('headings.hotelkeeperVisibility')}
          </span>
        </KoobTooltip>
      ),
      width: '14%',
      render: (request) => (
        <KoobInputSegmented
          value={request?.showToTa}
          onChange={(value) => handleToogle(value, request, 'ToTa')}
          options={[
            {
              label: t('show'),
              value: true,
            },
            {
              label: t('hide'),
              value: false,
            },
          ]}
          disabled={!isAllowedToManage()}
        />
      ),
    },
    {
      key: 'currency',
      label: (
        <span className="whitespace-normal text-center">
          {t('headings.hotelCurrency')}
        </span>
      ),
      width: '14%',
      render: (request) => (
        <KoobInputSelect
          onValueChange={(value) => {
            modifyCurrency(value, request?.hotel);
          }}
          options={currenciesOptions}
          loading={currenciesLoading}
          value={request?.hotel?.currency}
          disabled={!request?.hotel?.currency || !isAllowedToManage()}
        />
      ),
    },
    {
      key: 'experienceCurrency',
      label: (
        <span className="whitespace-normal text-center">
          {t('headings.experienceCurrency')}
        </span>
      ),
      width: '14%',
      render: (request) => (
        <KoobInputSelect
          onValueChange={(value) => {
            modifyCurrency(value, request?.experience);
          }}
          options={currenciesOptions}
          loading={currenciesLoading}
          value={request?.experience?.currency}
          disabled={!request?.experience?.currency || !isAllowedToManage()}
        />
      ),
    },
  ];

  const actions = (request) => {
    const requestHotelNotifications = getNotificationsByProductType(
      request?.hotel?.id,
      NotificationType.TO_CONNECTION_REQUEST_HOTEL_REQUESTED,
    );
    const requestExperienceNotifications = getNotificationsByProductType(
      request?.experience?.id,
      NotificationType.TO_CONNECTION_REQUEST_EXPERIENCE_REQUESTED,
    );

    return (
      <KoobTableActionMenu
        icon={<i className="fa-regular fa-gear" />}
        label={t('misc:actions')}
        id={`${request?.toOrganization?.name?.toLowerCase()}-menu-setting`}
      >
        {request?.hotel?.id && (
          <KoobTableActionMenuItem
            icon="fa-regular fa-bed"
            to={`${request?.hotel?.id}/hotels`}
            onClick={() => {
              readConnectionRequestNotifications(requestHotelNotifications);
            }}
          >
            {t('hotelSetting')}
          </KoobTableActionMenuItem>
        )}

        {request?.experience?.id && (
          <KoobTableActionMenuItem
            icon="fa-regular fa-layer-group"
            to={`${request?.experience?.id}/experiences`}
            onClick={() => {
              readConnectionRequestNotifications(
                requestExperienceNotifications,
              );
            }}
          >
            {t('experienceSetting')}
          </KoobTableActionMenuItem>
        )}

        {request?.template?.id && (
          <KoobTableActionMenuItem
            icon="fa-regular fa-copy"
            to={`${request?.template?.id}/templates`}
          >
            {t('templateSetting')}
          </KoobTableActionMenuItem>
        )}
      </KoobTableActionMenu>
    );
  };

  return (
    <>
      <Header
        isAllowedToManage={isAllowedToManage()}
        organizations={organizationOptions}
        selectedOrganization={
          selectedDistributionOrganization ?? selectedOrganization
        }
        onOrganizationChange={handleOrganizationChange}
      />

      <Container variant="main" size="full">
        <KoobListPageLayout
          width="wide"
          subtitle={t('misc:resultsCount', { count: requests.length })}
          search={<LocationAwareSearch name="search" />}
          toolbarActions={
            isAllowedToManage() && (
              <Link to="/to-connection-request/bulk-edit">
                <KoobButton variant="primary">
                  <i className="far fa-list-check mr-1.5" />
                  {t('bulkEditButton')}
                </KoobButton>
              </Link>
            )
          }
        >
          <KoobDataTable
            columns={columns}
            data={requests}
            actions={actions}
            loading={isLoading}
            sort={sort}
            onSortChange={onSortChange}
          />
        </KoobListPageLayout>
      </Container>
    </>
  );
};

export default ToConnectionRequestList;
